import React from "react";
import {connect} from "react-redux";
import {ButtonBack, ButtonNext, CarouselProvider, DotGroup, Slide, Slider,} from "pure-react-carousel";
import queryString from "query-string";
import {productoActions} from "../../_actions";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import productoServiceTs from "../../_services/cart/producto.service";
import vendedorService from "../../_services/cart/vendedor.service";
import ReactStars from "react-rating-stars-component";
import {Link} from "react-router-dom";

import WidgetMap from "./utils/WidgetMap";
import CarrouselProductosRel from "./utils/CarrouselProductosRel";
import Moment from "react-moment";
import imageService from "../../_services/cart/image.service";
import {actualizarMetaTags, LoaderGeneral, nombreVendedor, restaurarMetaTags} from "../../_helpers";

/** Cuantas reseñas se muestran antes del "ver todas". */
const RESENIAS_VISIBLES = 5;

/**
 * La tienda publica de un vendedor.
 *
 * <h3>Que es "Mi tienda"</h3>
 * Esta pagina es la portada —quien es, donde esta, como contactarlo, que opinan los que le
 * compraron— y el catalogo completo vive en `/tienda/{id}-{slug}/catalogo`, que es el Listador del
 * sitio con el filtro de vendedor puesto. Se separan a proposito: duplicar acá la grilla con sus
 * filtros, su orden y su paginado seria mantener dos listados que tienen que comportarse igual, y
 * el del sitio ya sabe buscar por texto, filtrar por marca y ordenar por precio. Acá va una muestra
 * en carrusel y un boton que lleva al listado con todo.
 *
 * <h3>La URL</h3>
 * La canonica es `/tienda/{id}-{slug}`; `/vendedorDetail?id=N` sigue entrando por compatibilidad
 * con los links ya compartidos. Las dos caen acá, y el canonical que escribe `actualizarMetaTags`
 * apunta siempre a la nueva.
 */
class VendedorDetail extends React.Component {
    state = {
        appName: process.env.PUBLIC_URL,
        otrosProductosVendedor: [],
        /*
         * Tres estados, no dos: `undefined` es "todavia cargando" y `null` es "esta tienda no
         * existe". Antes el estado inicial era `{}`, que es truthy, asi que la pantalla mostraba el
         * esqueleto de un vendedor vacio y el cartel de "no encontrado" no aparecia nunca.
         */
        vendedor: undefined,
        verTodasResenias: false,
        /*
         * La foto del avatar no cargo (S3 caido, un archivo borrado a mano, una URL vieja). Es
         * contenido que sube el vendedor, asi que puede romperse en cualquier momento: sin esto
         * la cabecera de la tienda abre con el icono de imagen rota del navegador.
         */
        avatarRoto: false,
    };

    componentDidMount() {
        window.scrollTo(0, 0);
        this.cargar();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location?.pathname !== this.props.location?.pathname ||
            prevProps.location?.search !== this.props.location?.search) {
            this.cargar();
        }
    }

    componentWillUnmount() {
        restaurarMetaTags();
    }

    /** El id sale del slug (`/tienda/1-...`) o del `?id=` de la URL vieja. */
    vendedorId = () => {
        const {slug} = this.props.match?.params || {};

        if (slug) return vendedorService.idDesdeSlug(slug);

        return queryString.parse(this.props.location.search).id;
    };

    cargar = () => {
        const id = this.vendedorId();

        if (!id) {
            this.setState({vendedor: null});
            return;
        }

        vendedorService.traerDataVendedor(id, this.setVendedor);
        productoServiceTs.traerUltimosProductosPorVendedorByVendedorId(id, this.setOtrosProductos);
    };

    setOtrosProductos = (otrosProductosVendedor) => {
        this.setState({otrosProductosVendedor: otrosProductosVendedor});
    };

    setVendedor = (vendedor) => {
        this.setState({vendedor: vendedor ?? null, verTodasResenias: false, avatarRoto: false});

        if (vendedor) this.actualizarSeo(vendedor);
    };

    /**
     * Metadatos de la pestaña y del link compartido.
     *
     * <p>El prerender del backend cubre a los bots que no ejecutan JS; esto cubre al resto —la
     * pestaña, el historial, y los clientes que sí ejecutan JS antes de leer el head.
     */
    actualizarSeo = (vendedor) => {
        const nombre = nombreVendedor(vendedor);
        const ubicacion = this.ubicacion(vendedor);
        const imagen = this.imagenes(vendedor)[0]?.url;

        actualizarMetaTags({
            titulo: `${nombre} — Repuestos usados${ubicacion ? ` en ${ubicacion}` : ""}`,
            descripcion: vendedor.descripcionPerfil
                || `${vendedor.publicaciones || "Todas las"} publicaciones de repuestos usados${
                    ubicacion ? ` en ${ubicacion}` : ""
                }. Consultá al vendedor y comprá por DeAutopartes.`,
            imagen: imagen,
            url: window.location.origin + vendedorService.crearUrlTienda(vendedor),
        });
    };

    crearLink = (link) => (link.includes("https://") ? link : "https://" + link);

    imagenes = (vendedor) => {
        if (vendedor?.imagenes && vendedor.imagenes.length > 0) return vendedor.imagenes;

        return [{url: this.state.appName + "/images/banner-bg.jpg", type: "image/jpeg"}];
    };

    /** "San Martín, Buenos Aires" — lo que se lee de un vistazo debajo del nombre. */
    ubicacion = (vendedor) =>
        [vendedor?.ciudad, vendedor?.provincia?.nombre].filter(Boolean).join(", ");

    /** La inicial del nombre, para el avatar cuando el vendedor no cargó logo. */
    inicial = (vendedor) => (nombreVendedor(vendedor) || "?").trim().charAt(0).toUpperCase();

    /** El promedio que muestra la cabecera. Null si todavía no lo calificó nadie. */
    rating = (vendedor) => {
        const valor = Number(vendedor?.rating ?? 0);
        return valor > 0 ? valor : null;
    };

    renderRedes = (vendedor) => {
        const redes = [
            vendedor.numeroCelular && {
                href: productoActions.crearLinkWhatsappAUsuario(vendedor),
                icono: "fa-brands fa-whatsapp",
                titulo: "Consultar por WhatsApp",
                tipo: "message",
            },
            vendedor.linkInstagram && {
                href: this.crearLink(vendedor.linkInstagram),
                icono: "fa-brands fa-instagram",
                titulo: "Instagram",
                tipo: "complementary",
            },
            vendedor.linkFacebook && {
                href: this.crearLink(vendedor.linkFacebook),
                icono: "fa-brands fa-facebook-f",
                titulo: "Facebook",
                tipo: "complementary",
            },
            vendedor.linkWeb && {
                href: this.crearLink(vendedor.linkWeb),
                icono: "fa-solid fa-globe",
                titulo: "Sitio web",
                tipo: "complementary",
            },
        ].filter(Boolean);

        if (redes.length === 0) return null;

        return (
            <div className="tienda__redes">
                {redes.map((red) => (
                    <a key={red.titulo} className="button tienda__red" data-type={red.tipo}
                       href={red.href} target="_blank" rel="noreferrer"
                       title={red.titulo} aria-label={red.titulo}>
                        <FontAwesomeIcon icon={red.icono} size="lg"/>
                        <span className="tienda__red-label">{red.titulo}</span>
                    </a>
                ))}
            </div>
        );
    };

    render() {
        const {vendedor, otrosProductosVendedor, verTodasResenias} = this.state;

        if (vendedor === undefined) {
            return (
                <main>
                    <div className="container__dap py-5">
                        <LoaderGeneral grabando={true}/>
                    </div>
                </main>
            );
        }

        if (vendedor === null) {
            return (
                <main>
                    <div className="container__dap tienda__vacia py-5">
                        <FontAwesomeIcon icon="fa-solid fa-store-slash" size="2x"/>
                        <h2>Esta tienda no está disponible</h2>
                        <p>Puede haber sido dada de baja o suspendida.</p>
                        <Link className="button" data-type="primary" to="/">Ver todas las autopartes</Link>
                    </div>
                </main>
            );
        }

        const nombre = nombreVendedor(vendedor);
        const ubicacion = this.ubicacion(vendedor);
        const rating = this.rating(vendedor);
        const resenias = vendedor.sellerReviews || [];
        const reseniasAMostrar = verTodasResenias ? resenias : resenias.slice(0, RESENIAS_VISIBLES);
        const imagenes = this.imagenes(vendedor);
        const urlCatalogo = vendedorService.crearUrlCatalogo(vendedor);

        return (
            <main>
                {/*
                    Cabecera de la tienda.

                    La foto de portada de fondo y encima el nombre, la ubicación y la calificación:
                    es lo que contesta "¿a quién le estoy comprando?" sin tener que bajar. Antes la
                    página abría con un <h2> suelto y un carrusel de fotos que ocupaba media
                    pantalla sin decir nada.
                */}
                <header className="tienda__hero">
                    <div className="tienda__hero-fondo"
                         style={{backgroundImage: `url(${imagenes[0].url})`}}
                         aria-hidden="true"/>

                    <div className="container__dap tienda__hero-contenido">
                        <div className="tienda__avatar" aria-hidden="true">
                            {imageService.isImage(imagenes[0]) && !this.state.avatarRoto
                                ? <img src={imagenes[0].url} alt=""
                                       onError={() => this.setState({avatarRoto: true})}/>
                                : <span>{this.inicial(vendedor)}</span>}
                        </div>

                        <div className="tienda__identidad">
                            <p className="tienda__etiqueta">
                                <FontAwesomeIcon icon="fa-solid fa-store"/> Tienda oficial
                            </p>
                            <h1>{nombre}</h1>

                            {ubicacion && (
                                <p className="tienda__ubicacion">
                                    <FontAwesomeIcon icon="fa-solid fa-location-dot"/> {ubicacion}
                                </p>
                            )}

                            {/*
                                Las tres cifras que le dan tamaño a la tienda. La calificación sólo
                                aparece si alguien calificó: "0 de 5" en una tienda nueva miente
                                —dice que le fue mal, no que todavía no le compró nadie.
                            */}
                            <div className="tienda__stats">
                                <div className="tienda__stat">
                                    <strong>{vendedor.publicaciones ?? 0}</strong>
                                    <span>{vendedor.publicaciones === 1 ? "publicación" : "publicaciones"}</span>
                                </div>

                                {rating !== null && (
                                    <div className="tienda__stat">
                                        <strong>
                                            {rating.toFixed(1)}
                                            <FontAwesomeIcon icon="fa-solid fa-star" className="tienda__stat-estrella"/>
                                        </strong>
                                        <span>calificación</span>
                                    </div>
                                )}

                                <div className="tienda__stat">
                                    <strong>{resenias.length}</strong>
                                    <span>{resenias.length === 1 ? "reseña" : "reseñas"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="tienda__acciones">
                            {/*
                                La acción principal de la tienda: ver todo lo que vende. Lleva al
                                listado del sitio con el filtro de vendedor puesto, así el catálogo
                                se recorre con los mismos filtros, orden y paginado que el resto.
                            */}
                            <Link className="button tienda__cta" data-type="primary" to={urlCatalogo}>
                                <FontAwesomeIcon icon="fa-solid fa-boxes-stacked"/>
                                <span>Ver todo el catálogo</span>
                            </Link>

                            {this.renderRedes(vendedor)}
                        </div>
                    </div>
                </header>

                <div className="container__dap tienda__cuerpo">
                    <div className="tienda__principal">
                        {vendedor.descripcionPerfil && (
                            <section className="tienda__bloque">
                                <h2>Sobre la tienda</h2>
                                <p className="tienda__descripcion">{vendedor.descripcionPerfil}</p>
                            </section>
                        )}

                        {otrosProductosVendedor && otrosProductosVendedor.length > 0 && (
                            <section className="tienda__bloque">
                                <div className="tienda__bloque-header">
                                    <h2>Últimas publicaciones</h2>
                                    <Link className="tienda__link" to={urlCatalogo}>
                                        Ver todas <FontAwesomeIcon icon="fa-solid fa-arrow-right"/>
                                    </Link>
                                </div>

                                <CarrouselProductosRel
                                    productos={otrosProductosVendedor}
                                    plano
                                />
                            </section>
                        )}

                        <section className="tienda__bloque">
                            <div className="tienda__bloque-header">
                                <h2>Reseñas</h2>
                                {rating !== null && (
                                    <ReactStars value={rating} edit={false} size={20} isHalf={true}
                                                activeColor="#ffd700"/>
                                )}
                            </div>

                            {resenias.length === 0 ? (
                                <p className="tienda__vacio-texto">
                                    Todavía no hay reseñas de esta tienda.
                                </p>
                            ) : (
                                <>
                                    <ul className="tienda__resenias">
                                        {reseniasAMostrar.map((review) => (
                                            <li className="tienda__resenia" key={review.id}>
                                                <div className="tienda__resenia-head">
                                                    <span className="tienda__resenia-autor">
                                                        {review.buyerMemberAccount?.username}
                                                    </span>
                                                    <ReactStars value={review.rating} edit={false} size={16}
                                                                isHalf={true} activeColor="#ffd700"/>
                                                    <Moment className="tienda__resenia-fecha"
                                                            date={review.createdInstant} format="DD/MM/YYYY"/>
                                                </div>
                                                {review.observation && (
                                                    <p className="tienda__resenia-texto">{review.observation}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>

                                    {resenias.length > RESENIAS_VISIBLES && !verTodasResenias && (
                                        <button type="button" className="button" data-type="complementary"
                                                onClick={() => this.setState({verTodasResenias: true})}>
                                            Ver las {resenias.length} reseñas
                                        </button>
                                    )}
                                </>
                            )}
                        </section>
                    </div>

                    {/*
                        Columna lateral: fotos del local, dirección y mapa. Es contexto —ayuda a
                        decidir, no es lo primero que se busca— así que en escritorio va al costado
                        y en mobile cae debajo del contenido principal.
                    */}
                    <aside className="tienda__lateral">
                        {vendedor.imagenes && vendedor.imagenes.length > 0 && (
                            <section className="tienda__bloque">
                                <h2>El local</h2>

                                {/*
                                    Galería con los controles encima de la foto, no debajo.

                                    Los de `slider_buttons` son los del carrusel principal de la
                                    home: botones grandes con la clase `button` y numeritos en los
                                    puntos. A ese tamaño, en una columna de 22rem, ocupaban más que
                                    la foto y los "1 2" se leían como si fueran contenido.
                                */}
                                <div className="tienda__galeria">
                                <CarouselProvider
                                    naturalSlideWidth={100}
                                    naturalSlideHeight={60}
                                    visibleSlides={1}
                                    infinite={vendedor.imagenes.length > 1}
                                    lockOnWindowScroll={true}
                                    totalSlides={vendedor.imagenes.length}
                                    isIntrinsicHeight={true}
                                >
                                    <Slider className="slider">
                                        {vendedor.imagenes.map((img, index) => (
                                            <Slide className="slide" index={index} key={index}>
                                                {imageService.isImage(img)
                                                    ? <img className="d-block w-100" src={img.url} alt={img.tipo}
                                                           onError={(e) => {
                                                               // Sin esto queda el icono de rota y el texto del alt
                                                               // suelto en el medio del bloque. El banner generico es
                                                               // el mismo fallback que usa el listado.
                                                               e.target.onerror = null;
                                                               e.target.src = this.state.appName + "/images/banner-bg.jpg";
                                                           }}/>
                                                    : <video controls={true} className="d-block w-100" src={img.url}/>}
                                            </Slide>
                                        ))}
                                    </Slider>

                                    {/* Con una sola foto los controles no tienen a dónde llevar. */}
                                    {vendedor.imagenes.length > 1 && (
                                        <>
                                            {/* Sin `className="button"`: esa clase es la que los
                                                hacía enormes. Son controles de la foto, no acciones
                                                de la página. */}
                                            <ButtonBack className="tienda__galeria-flecha tienda__galeria-flecha--prev"
                                                        aria-label="Foto anterior">
                                                <FontAwesomeIcon icon="fa-solid fa-chevron-left"/>
                                            </ButtonBack>
                                            <ButtonNext className="tienda__galeria-flecha tienda__galeria-flecha--next"
                                                        aria-label="Foto siguiente">
                                                <FontAwesomeIcon icon="fa-solid fa-chevron-right"/>
                                            </ButtonNext>

                                            {/* Sin `dotNumbers`: puntos, no un "1 2" suelto. */}
                                            <DotGroup className="tienda__galeria-dots"/>
                                        </>
                                    )}
                                </CarouselProvider>
                                </div>
                            </section>
                        )}

                        {vendedor.latitud && vendedor.longitud && (
                            <section className="tienda__bloque">
                                <h2>Dónde está</h2>

                                <div className="tienda__mapa">
                                    <WidgetMap
                                        markerName={nombre}
                                        lat={parseFloat(vendedor.latitud)}
                                        lng={parseFloat(vendedor.longitud)}
                                        width={"100%"}
                                        height={"280px"}
                                    />
                                </div>

                                <p className="tienda__direccion">
                                    {[
                                        vendedor.direccion,
                                        vendedor.ciudad,
                                        vendedor.provincia?.nombre,
                                        vendedor.codigoPostal,
                                    ].filter(Boolean).join(", ")}
                                </p>

                                <a className="button" data-type="complementary" target="_blank" rel="noreferrer"
                                   href={`https://www.google.com/maps/search/?api=1&query=${vendedor.latitud},${vendedor.longitud}`}>
                                    <FontAwesomeIcon icon="fa-solid fa-diamond-turn-right"/>
                                    <span>Cómo llegar</span>
                                </a>
                            </section>
                        )}
                    </aside>
                </div>
            </main>
        );
    }
}

function mapState(state) {
    return {};
}

const actionCreators = {};

const connectedVendedorDetail = connect(
    mapState,
    actionCreators
)(VendedorDetail);
export {connectedVendedorDetail as VendedorDetail};
